import call from "./call";
import {
  Deal,
  DealData,
  DealListFilters,
  DealListResponse,
  DealSummary,
  DealUser,
  EditorNewsItem,
  EditorNewsListResponse,
  InternalDeal,
  InternalDealListFilters,
  InternalDealListResponse,
  InternalDealSummary,
  NewsCategory,
  NewsItem,
  NewsListResponse,
  NewsPayload,
  NewsStatus,
  ProcessingPayload,
} from "../Models/ambassador";

const DEALS = "/v1/ambassador/deals";
const NEWS = "/v1/ambassador/news";

// The ambassador's own deals

export const getMyDeals = async (
  filters: DealListFilters,
): Promise<DealListResponse> => {
  const response = await call.get<DealListResponse>(`${DEALS}/my/`, {
    params: filters,
    snack: { error: "ambassador.dealsFetchError" },
  });
  return response.data;
};

export const getMyDealSummary = async (): Promise<DealSummary> => {
  const response = await call.get<DealSummary>(`${DEALS}/my/summary/`, {
    snack: { error: "ambassador.dealsFetchError" },
  });
  return response.data;
};

export const getMyDeal = async (dealId: number): Promise<Deal> => {
  const response = await call.get<Deal>(`${DEALS}/my/${dealId}/`, {
    snack: {
      error: "ambassador.dealFetchError",
      404: "ambassador.dealNotFound",
    },
  });
  return response.data;
};

// Moving between the form steps saves the draft too; only an explicit save
// (`notify`) confirms it with a message.
export const createDraft = async (
  data: Partial<DealData>,
  notify: boolean,
): Promise<Deal> => {
  const response = await call.post<Deal>(`${DEALS}/my/`, data, {
    snack: {
      success: notify ? "ambassador.draftSaveSuccess" : undefined,
      error: "ambassador.draftSaveError",
    },
  });
  return response.data;
};

export const updateDraft = async (
  dealId: number,
  data: Partial<DealData>,
  notify: boolean,
): Promise<Deal> => {
  const response = await call.put<Deal>(`${DEALS}/my/${dealId}/`, data, {
    snack: {
      success: notify ? "ambassador.draftSaveSuccess" : undefined,
      error: "ambassador.draftSaveError",
      409: "ambassador.dealLocked",
    },
  });
  return response.data;
};

export const deleteDraft = async (dealId: number): Promise<void> => {
  await call.delete(`${DEALS}/my/${dealId}/`, {
    snack: {
      success: "ambassador.draftDeleteSuccess",
      error: "ambassador.draftDeleteError",
    },
  });
};

export const submitDeal = async (
  dealId: number,
  data: Partial<DealData>,
): Promise<Deal> => {
  const response = await call.post<Deal>(
    `${DEALS}/my/${dealId}/submit/`,
    data,
    {
      snack: {
        success: "ambassador.submitSuccess",
        error: "ambassador.submitError",
        409: "ambassador.dealLocked",
        422: "ambassador.submitIncomplete",
      },
    },
  );
  return response.data;
};

export const withdrawDeal = async (
  dealId: number,
  reason: string,
): Promise<Deal> => {
  const response = await call.post<Deal>(
    `${DEALS}/my/${dealId}/withdraw/`,
    { reason },
    {
      snack: {
        success: "ambassador.withdrawSuccess",
        error: "ambassador.withdrawError",
      },
    },
  );
  return response.data;
};

export const addMyDealMessage = async (
  dealId: number,
  message: string,
): Promise<Deal> => {
  const response = await call.post<Deal>(
    `${DEALS}/my/${dealId}/messages/`,
    { message },
    {
      snack: {
        success: "ambassador.messageSuccess",
        error: "ambassador.messageError",
      },
    },
  );
  return response.data;
};

// como's deal management

export const getDeals = async (
  filters: InternalDealListFilters,
): Promise<InternalDealListResponse> => {
  const response = await call.get<InternalDealListResponse>(`${DEALS}/`, {
    params: filters,
    snack: { error: "ambassador.dealsFetchError" },
  });
  return response.data;
};

export const getDealSummary = async (): Promise<InternalDealSummary> => {
  const response = await call.get<InternalDealSummary>(`${DEALS}/summary/`, {
    snack: { error: "ambassador.dealsFetchError" },
  });
  return response.data;
};

export const getDealOwners = async (): Promise<DealUser[]> => {
  const response = await call.get<{ owners: DealUser[] }>(`${DEALS}/owners/`, {
    snack: { error: "ambassador.ownersFetchError" },
  });
  return response.data.owners;
};

export const getDeal = async (dealId: number): Promise<InternalDeal> => {
  const response = await call.get<InternalDeal>(`${DEALS}/${dealId}/`, {
    snack: {
      error: "ambassador.dealFetchError",
      404: "ambassador.dealNotFound",
    },
  });
  return response.data;
};

export const correctDeal = async (
  dealId: number,
  data: Partial<DealData>,
): Promise<InternalDeal> => {
  const response = await call.put<InternalDeal>(`${DEALS}/${dealId}/`, data, {
    snack: {
      success: "ambassador.correctionSuccess",
      error: "ambassador.correctionError",
    },
  });
  return response.data;
};

export const processDeal = async (
  dealId: number,
  data: ProcessingPayload,
): Promise<InternalDeal> => {
  const response = await call.put<InternalDeal>(
    `${DEALS}/${dealId}/processing/`,
    data,
    {
      snack: {
        success: "ambassador.processingSuccess",
        error: "ambassador.processingError",
        409: "ambassador.processingConflict",
      },
    },
  );
  return response.data;
};

// News

export const getNews = async (filters: {
  page: number;
  page_size?: number;
  category?: NewsCategory | null;
}): Promise<NewsListResponse> => {
  const response = await call.get<NewsListResponse>(`${NEWS}/`, {
    params: filters,
    snack: { error: "ambassador.newsFetchError" },
  });
  return response.data;
};

export const getNewsItem = async (newsId: number): Promise<NewsItem> => {
  const response = await call.get<NewsItem>(`${NEWS}/${newsId}/`, {
    snack: {
      error: "ambassador.newsFetchError",
      404: "ambassador.newsNotFound",
    },
  });
  return response.data;
};

export const getEditorNews = async (filters: {
  page: number;
  page_size?: number;
  status?: NewsStatus | null;
}): Promise<EditorNewsListResponse> => {
  const response = await call.get<EditorNewsListResponse>(`${NEWS}/manage/`, {
    params: filters,
    snack: { error: "ambassador.newsFetchError" },
  });
  return response.data;
};

export const createNewsItem = async (
  data: NewsPayload,
): Promise<EditorNewsItem> => {
  const response = await call.post<EditorNewsItem>(`${NEWS}/manage/`, data, {
    snack: {
      success: "ambassador.newsSaveSuccess",
      error: "ambassador.newsSaveError",
    },
  });
  return response.data;
};

export const updateNewsItem = async (
  newsId: number,
  data: Partial<NewsPayload>,
): Promise<EditorNewsItem> => {
  const response = await call.put<EditorNewsItem>(
    `${NEWS}/manage/${newsId}/`,
    data,
    {
      snack: {
        success: "ambassador.newsSaveSuccess",
        error: "ambassador.newsSaveError",
      },
    },
  );
  return response.data;
};

export const deleteNewsItem = async (newsId: number): Promise<void> => {
  await call.delete(`${NEWS}/manage/${newsId}/`, {
    snack: {
      success: "ambassador.newsDeleteSuccess",
      error: "ambassador.newsDeleteError",
    },
  });
};

// Documents of a deal. The ambassador reaches them through their own deals,
// como through deal management; both share one library.

export type DealSide = "ambassador" | "como";

const dealPath = (side: DealSide, dealId: number) =>
  side === "ambassador" ? `${DEALS}/my/${dealId}` : `${DEALS}/${dealId}`;

/** Address of a document; images and PDFs are served inline. */
export const dealDocumentUrl = (
  side: DealSide,
  dealId: number,
  documentId: number,
) => `${dealPath(side, dealId)}/documents/${documentId}/`;

const documentSnack = {
  success: "ambassador.documentUploadSuccess",
  error: "ambassador.documentUploadError",
  413: "ambassador.documentTooLarge",
  415: "ambassador.documentType",
};

export const uploadDealDocuments = async <T>(
  side: DealSide,
  dealId: number,
  files: File[],
): Promise<T> => {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  const response = await call.post<T>(
    `${dealPath(side, dealId)}/documents/`,
    form,
    { snack: documentSnack },
  );
  return response.data;
};

export const deleteDealDocument = async <T>(
  side: DealSide,
  dealId: number,
  documentId: number,
): Promise<T> => {
  const response = await call.delete<T>(
    dealDocumentUrl(side, dealId, documentId),
    {
      snack: {
        success: "ambassador.documentDeleteSuccess",
        error: "ambassador.documentDeleteError",
      },
    },
  );
  return response.data;
};

export const downloadDealDocument = (
  side: DealSide,
  dealId: number,
  documentId: number,
) =>
  call.get(dealDocumentUrl(side, dealId, documentId), {
    params: { download: 1 },
    responseType: "blob",
    snack: { error: "ambassador.documentDownloadError" },
  });
